/**
 * ExtranetForgotPassword — Fase 4 Extranet KB
 */
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { resolveAuthError, type AuthErrorInfo } from '@/lib/extranet/authErrors';
import { ExtranetAuthAlert } from '@/components/kb/extranet/ExtranetAuthAlert';

export default function ExtranetForgotPassword() {
  const { tenantSlug = '' } = useParams<{ tenantSlug: string }>();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [alertInfo, setAlertInfo] = useState<AuthErrorInfo | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setAlertInfo(null);
    setSubmitting(true);
    const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
      redirectTo: `${window.location.origin}/extranet/${tenantSlug}/redefinir-senha`,
    });
    setSubmitting(false);
    if (error) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const anyErr = error as any;
      // Se for rate-limit, avisamos; caso contrário, mostramos a mesma
      // confirmação genérica (anti-enumeração).
      const info = resolveAuthError({
        code: anyErr?.code,
        status: anyErr?.status,
        message: error.message,
      });
      if (info.code === 'over_request_rate_limit' || info.code === 'over_email_send_rate_limit') {
        setAlertInfo(info);
        return;
      }
    }
    setSent(true);
  };

  return (
    <div className="min-h-screen bg-muted/30 flex items-center justify-center p-4">
      <Helmet>
        <title>Recuperar senha</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <Card className="w-full max-w-md p-6 space-y-5">
        <h1 className="text-xl font-semibold text-center">Recuperar senha</h1>
        {sent ? (
          <div className="space-y-3 text-center">
            <p className="text-sm text-muted-foreground">
              Se o e-mail existir, enviamos um link para redefinir a senha.
            </p>
            <Button asChild variant="outline" className="w-full">
              <Link to={`/extranet/${tenantSlug}/login`}>Voltar ao login</Link>
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-3">
            <ExtranetAuthAlert info={alertInfo} hrefPrefix={`/extranet/${tenantSlug}`} />
            <div className="space-y-1.5">
              <Label htmlFor="email">E-mail</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                maxLength={255}
              />
            </div>
            <Button type="submit" className="w-full" disabled={submitting}>
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Enviar link
            </Button>
            <div className="text-center text-sm">
              <Link to={`/extranet/${tenantSlug}/login`} className="text-muted-foreground hover:text-foreground">
                Voltar ao login
              </Link>
            </div>
          </form>
        )}
      </Card>
    </div>
  );
}
